/**
 * How gestures compose (docs/ops-protocol.md, "Gesture spaces and
 * composition"): the order the wrappers on a pointer's way to the root are
 * asked in, and the taps a `tapgesture` with `count` collects. Pure, so the
 * rules are tested in Node; src/interaction.ts and src/renderer.ts apply them.
 */

import type { GestureMode } from './protocol';

/** A behaviour that could own a pointer: a `gesture` element's carries its mode. */
export interface Wrapper {
  kind: string;
  mode?: GestureMode;
}

/** The double-tap interval: a single tap beside a double tap waits this long. */
export const DOUBLE_TAP_MS = 300;

/**
 * Puts a wrapper found on the way up into `out`, which lists the wrappers
 * found so far innermost first, from `wrapperStart` (what comes before them
 * is the system's: a row's grip, the back swipe). A `highPriority` gesture
 * goes in front of the wrappers inside it, as `.highPriorityGesture` wins
 * over the gestures of the views it wraps; an outer one found later goes in
 * front of it in turn. Everything else goes at the end, innermost first.
 */
export function placeWrapper<T extends Wrapper>(out: T[], candidate: T, wrapperStart: number): void {
  if (candidate.kind === 'gesture' && candidate.mode === 'highPriority') out.splice(wrapperStart, 0, candidate);
  else out.push(candidate);
}

/** `.simultaneousGesture`: runs beside the pointer's owner, never claiming. */
export function isSimultaneous(candidate: Wrapper): boolean {
  return candidate.kind === 'gesture' && candidate.mode === 'simultaneous';
}

/**
 * The taps a click lands: `multi` is the innermost `tapgesture` with a
 * `count` above one on the click's way up (null when none), `single` the
 * innermost of one tap; `detail` is what the firing needs of the click (its
 * point), the last click's when several make a tap. A click with no `multi`
 * fires `single` at once. With one, the clicks are counted: the `count`th
 * within the interval fires `multi`; should the interval pass first, `single`
 * fires instead (as iOS waits for a double tap before a single tap lands). A
 * click elsewhere ends the count, firing the pending single.
 */
export class TapCounter<T, D = void> {
  private pending: { multi: T; single: T | null; detail: D; taps: number; timer: unknown } | null = null;

  constructor(
    private readonly fire: (target: T, detail: D) => void,
    private readonly schedule: (run: () => void, ms: number) => unknown = (run, ms) => setTimeout(run, ms),
    private readonly cancel: (timer: unknown) => void = (timer) => clearTimeout(timer as number),
  ) {}

  click(multi: T | null, count: number, single: T | null, detail: D): void {
    if (this.pending && this.pending.multi !== multi) this.flush();
    if (!multi) {
      if (single) this.fire(single, detail);
      return;
    }
    if (this.pending) {
      this.pending.taps += 1;
      this.pending.detail = detail;
      if (this.pending.taps < count) return;
      this.cancel(this.pending.timer);
      this.pending = null;
      this.fire(multi, detail);
      return;
    }
    if (count <= 1) {
      this.fire(multi, detail);
      return;
    }
    const pending = { multi, single, detail, taps: 1, timer: null as unknown };
    pending.timer = this.schedule(() => {
      if (this.pending !== pending) return;
      this.pending = null;
      if (single) this.fire(single, pending.detail);
    }, DOUBLE_TAP_MS);
    this.pending = pending;
  }

  /** Ends a count early: the single tap it was holding fires (nothing when there was none). */
  flush(): void {
    const pending = this.pending;
    if (!pending) return;
    this.cancel(pending.timer);
    this.pending = null;
    if (pending.single) this.fire(pending.single, pending.detail);
  }
}
